import React, { useEffect, useState } from 'react';
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Collapse,
    Box,
    Typography,
    Checkbox,
    Paper,
    Button,
    Modal,
    TextField,
    IconButton,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Dialog,
    Card,
    CardContent,
    Divider,
    Chip,
    Grid,
    Stack
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import PaymentIcon from '@mui/icons-material/Payment';
import EventIcon from '@mui/icons-material/Event';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import { sendPaymentLink } from '../api/affiliateApi';
import SendIcon from '@mui/icons-material/Send';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import {
    getUserContracts,
    acceptContract,
    createPaymentHoliday,
    createPaymentHolidayDateRange,
    updatePaymentHoliday,
    updateContract,
    deletePaymentHoliday,
    createTerminationRequest,
    updateTerminationRequest
} from '../api/contractApi';
import { getAffiliateById} from "../api/affiliateApi";
import { addCredit, getUserTransactions } from "../api/creditApi";
import ContractTermsModal from './ContractTermsModal';
import { sendMessageToAffiliate } from "../api/messageApi";
import MenuItem from "@mui/material/MenuItem";
import {useNavigate} from "react-router-dom";
import {formatCurrency} from "../utils/currency";

const addUtcMonthsClamped = (dateValue, months) => {
    const date = new Date(dateValue);
    const day = date.getUTCDate();
    const result = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
    const lastDayOfTargetMonth = new Date(Date.UTC(
        result.getUTCFullYear(),
        result.getUTCMonth() + 1,
        0
    )).getUTCDate();

    result.setUTCDate(Math.min(day, lastDayOfTargetMonth));
    return result;
};

const getTerminationMinimumDetails = (contract) => {
    const months = contract?.affiliate?.minimumContractValidityMonths ?? 2;
    const startDateValue = contract?.startDate || contract?.acceptedAt || contract?.createdAt;
    const parsedStartDate = new Date(startDateValue);
    const startDate = Number.isNaN(parsedStartDate.getTime()) ? new Date() : parsedStartDate;
    const minimumValidityDate = addUtcMonthsClamped(startDate, months);
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const earliestDate = minimumValidityDate > today ? minimumValidityDate : today;

    return {
        months,
        earliestDate: earliestDate.toISOString().slice(0, 10),
    };
};

export default function UserContracts({ user, affiliateId }) {
    const [contracts, setContracts] = useState([]);
    const [expandedRows, setExpandedRows] = useState({});
    const [acceptCheckbox, setAcceptCheckbox] = useState({});
    const [termsModalOpen, setTermsModalOpen] = useState(false);
    const [selectedContractTermsId, setSelectedContractTermsId] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Dialog states
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmDialogTitle, setConfirmDialogTitle] = useState('');
    const [confirmDialogMessage, setConfirmDialogMessage] = useState('');

    // Payment Holiday Modal state
    const [phModalOpen, setPhModalOpen] = useState(false);
    const [phContract, setPhContract] = useState(null);
    const [phData, setPhData] = useState({
        startDate: '',
        endDate: '',
        reason: '',
    });
    const [phValidationErrors, setPhValidationErrors] = useState([]);
    const [phEditingHoliday, setPhEditingHoliday] = useState(null);

    const navigate = useNavigate();

    // Lisame oleku dialoogi ja endDate haldamiseks
    const [openDeactivateDialog, setOpenDeactivateDialog] = useState(false);
    const [newEndDate, setNewEndDate] = useState("");
    const [contractToDeactivate, setContractToDeactivate] = useState(null);

    const [sendLinkDialogOpen, setSendLinkDialogOpen] = useState(false);
    const [contractToSendLink, setContractToSendLink] = useState(null);
    const [sendingPaymentLink, setSendingPaymentLink] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState('current'); // 'previous', 'current' or 'next'
    const [paymentPeriods, setPaymentPeriods] = useState({ previous: null, current: null, next: null });

    // Payment holiday approval follow-up modal
    const [phFollowUpModalOpen, setPhFollowUpModalOpen] = useState(false);
    const [phFollowUpData, setPhFollowUpData] = useState(null);
    const [returnAsCredit, setReturnAsCredit] = useState('yes');
    const [deductHolidayFee, setDeductHolidayFee] = useState('no');
    const [sendNewPaymentLinkChoice, setSendNewPaymentLinkChoice] = useState('no');
    const [sendNextPeriodPaymentLinkChoice, setSendNextPeriodPaymentLinkChoice] = useState('no');
    const [processingPhFollowUp, setProcessingPhFollowUp] = useState(false);
    const [processingPhActionId, setProcessingPhActionId] = useState(null);
    const [paymentCurrency, setPaymentCurrency] = useState("EUR");

    // Contract termination request modal
    const [terminationModalOpen, setTerminationModalOpen] = useState(false);
    const [terminationContract, setTerminationContract] = useState(null);
    const [terminationData, setTerminationData] = useState({
        requestedEndDate: '',
        reason: '',
    });
    const [submittingTermination, setSubmittingTermination] = useState(false);
    const [processingTerminationActionId, setProcessingTerminationActionId] = useState(null);
    const [terminationFollowUpOpen, setTerminationFollowUpOpen] = useState(false);
    const [terminationFollowUpContract, setTerminationFollowUpContract] = useState(null);

    const getContractCurrency = (contract) => contract?.affiliate?.paymentCurrency || paymentCurrency;
    const formatMoney = (value, contract = null) => formatCurrency(value, getContractCurrency(contract));

    useEffect(() => {
        if (user?.id) {
            loadContracts();
        }
    }, [user]);

    useEffect(() => {
        const loadAffiliateCurrency = async () => {
            if (!affiliateId) return;
            try {
                const affiliate = await getAffiliateById(affiliateId);
                setPaymentCurrency(affiliate?.paymentCurrency || "EUR");
            } catch (error) {
                setPaymentCurrency("EUR");
            }
        };

        loadAffiliateCurrency();
    }, [affiliateId]);

    const loadContracts = async () => {
        let affiliateIdForContract = 0;
        const role = localStorage.getItem('role');
        if ( role === "affiliate" ) {
            affiliateIdForContract = affiliateId;
        }

        const data = await getUserContracts(user.id, affiliateIdForContract);
        if (Array.isArray(data)) {
            setContracts(data);
        }
    };

    // Get training types as an array
    const getTrainingTypesArray = (trainingType) => {
        if (!trainingType) return [];
        try {
            // Try to parse as JSON first (for the new format)
            return JSON.parse(trainingType);
        } catch (e) {
            // Fallback for old format (comma-separated string)
            return trainingType.split(',');
        }
    };

    // Get color for training type chip
    const getTrainingTypeColor = (type) => {
        switch (type) {
            case 'WOD':
                return 'primary';
            case 'Weightlifting':
                return 'secondary';
            case 'Rowing':
                return 'success';
            case 'Gymnastics':
                return 'info';
            case 'Open Gym':
                return 'warning';
            case 'Cardio':
                return 'error';
            case 'All classes':
                return 'default';
            default:
                return 'default';
        }
    };

    const toggleRow = (id) => {
        setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleCheckboxChange = (contractId, checked) => {
        setAcceptCheckbox((prev) => ({ ...prev, [contractId]: checked }));
    };

    const handleAccept = async (contract) => {
        // Kontrollime, et affiliateId on olemas ja õiges formaadis
        let parsedAffiliateId = affiliateId;
        if (!parsedAffiliateId && contract && contract.affiliateId) {
            parsedAffiliateId = contract.affiliateId;
        }

        if (!parsedAffiliateId) {
            showErrorMessage('Missing affiliateId! Please contact the administrator.');
            return;
        }

        // Veendume, et affiliateId on number
        parsedAffiliateId = parseInt(parsedAffiliateId);

        if (isNaN(parsedAffiliateId)) {
            showErrorMessage('AffiliateId is not in the correct format! Please contact the administrator.');
            return;
        }



        // Suuname kasutaja checkout lehele lepingu andmetega
        navigate('/checkout', {
            state: {
                contract: contract,
                plan: {
                    id: 'contract-payment', // Spetsiaalne identifikaator lepingu maksete jaoks
                    name: `${contract.paymentType || 'Monthly'} Contract Payment`,
                    price: contract.paymentAmount,
                    trainingType: contract.trainingType,
                    affiliateId: contract.affiliateId, // Kasuta parsitud ID-d
                    validityDays: 31, // Standardne 30-päevane kehtivus esimesele maksele
                    sessions: 999, // Piisavalt suur arv, et kasutaja saaks käia nii palju kui tahab

                },
                affiliate: {
                    id: contract.affiliateId,
                    name: contract.affiliate?.name || 'Affiliate'
                },
                userData: user,
                isContractPayment: true
            }
        });
    };

    const openTerms = (contract) => {
        setSelectedContractTermsId('contract'); // siia reaalne contractTermsId, kui sul on
        setTermsModalOpen(true);
    };

    // Delete payment holiday handler
    const handleDeletePaymentHoliday = async (phId) => {
        showConfirmDialog(
            'Delete Payment Holiday',
            'Are you sure you want to delete this payment holiday? This action cannot be undone.',
            async () => {
                try {
                    const result = await deletePaymentHoliday(phId);
                    if (result && result.success) {
                        showSuccessMessage('Payment holiday deleted successfully!');
                        await loadContracts();
                    } else {
                        showErrorMessage('Error deleting payment holiday!');
                    }
                } catch (error) {
                    console.error('Error deleting payment holiday:', error);
                    showErrorMessage('Error deleting payment holiday!');
                }
            }
        );
    };

    const handleOpenTerminationModal = (contract) => {
        setTerminationContract(contract);
        setTerminationData({
            requestedEndDate: '',
            reason: '',
        });
        setTerminationModalOpen(true);
    };

    const handleCloseTerminationModal = () => {
        setTerminationModalOpen(false);
        setTerminationContract(null);
        setTerminationData({ requestedEndDate: '', reason: '' });
        setSubmittingTermination(false);
    };

    const handleTerminationDataChange = (e) => {
        const { name, value } = e.target;
        setTerminationData((prev) => ({ ...prev, [name]: value }));
    };

    const handleTerminationSave = async () => {
        if (!terminationContract) return;

        if (!terminationData.requestedEndDate) {
            showErrorMessage('Please select a desired end date');
            return;
        }

        const {months, earliestDate} = getTerminationMinimumDetails(terminationContract);
        if (terminationData.requestedEndDate < earliestDate) {
            showErrorMessage(
                `The earliest allowed end date is ${formatDate(earliestDate)}. ` +
                `The contract must remain valid for at least ${months} month${months === 1 ? '' : 's'}.`
            );
            return;
        }

        setSubmittingTermination(true);
        try {
            const result = await createTerminationRequest(
                terminationContract.id,
                terminationData.requestedEndDate,
                terminationData.reason
            );

            if (result && result.success) {
                showSuccessMessage('Your request was submitted successfully!');

                const getAffiliateData = await getAffiliateById(terminationContract.affiliateId);
                const endDate = formatDate(terminationData.requestedEndDate);
                const subject = `IronTrack – membership end date from ${user.fullName}`;
                const body = `
<p><strong>${user.fullName}</strong> submitted a membership end date in IronTrack.</p>

<p><strong>Contract:</strong> ${terminationContract.contractType || 'Membership Contract'}</p>
<p><strong>Requested end date:</strong> ${endDate}</p>
${terminationData.reason ? `<p><strong>Reason:</strong> ${terminationData.reason}</p>` : '<p><strong>Reason:</strong> —</p>'}

<p>Go to Irontrack page -> My affiliate -> Contracts and choose <strong>${user.fullName}</strong> contract to review this.</p>`;

                await sendMessageToAffiliate(
                    user.email,
                    getAffiliateData.email,
                    subject,
                    body
                );
            } else {
                showErrorMessage(result?.error || 'Error creating request!');
            }
        } catch (error) {
            console.error('Error creating end-date request:', error);
            showErrorMessage('Error creating request!');
        } finally {
            setSubmittingTermination(false);
            handleCloseTerminationModal();
            await loadContracts();
        }
    };

    const handleUpdateTerminationStatus = async (request, contract, newStatus) => {
        setProcessingTerminationActionId(request.id);
        try {
            const result = await updateTerminationRequest(request.id, { accepted: newStatus });
            if (result && result.success) {
                if (newStatus === 'approved') {
                    setTerminationFollowUpContract(contract);
                    setTerminationFollowUpOpen(true);
                } else {
                    showSuccessMessage('Request was not approved.');
                }
                await loadContracts();
            } else {
                showErrorMessage(result?.error || 'Error updating request status!');
            }
        } catch (error) {
            console.error('Error updating end-date request:', error);
            showErrorMessage('Error updating request!');
        } finally {
            setProcessingTerminationActionId(null);
        }
    };

    const handleCloseTerminationFollowUp = () => {
        setTerminationFollowUpOpen(false);
        setTerminationFollowUpContract(null);
    };

    const handleTerminationFollowUpSendLink = () => {
        const contract = terminationFollowUpContract;
        handleCloseTerminationFollowUp();
        if (contract) {
            handleOpenSendLinkDialog(contract);
        }
    };

    const toDateInputValue = (value) => {
        if (!value) return '';
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
            return value.slice(0, 10);
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        return date.toISOString().slice(0, 10);
    };

    // Avab Payment Holiday modali ja salvestab state-sse lepingu, mille jaoks andmeid kogume
    const handleOpenPhModal = (contract, holiday = null) => {
        setPhContract(contract);
        setPhEditingHoliday(holiday);
        const startDate = holiday ? toDateInputValue(holiday.startDate) : '';
        const endDate = holiday ? toDateInputValue(holiday.endDate) : '';
        const reason = holiday?.reason || '';
        setPhData({
            startDate,
            endDate,
            reason,
        });
        setPhValidationErrors(
            holiday
                ? validatePaymentHolidayDates(startDate, endDate, contract, {
                    skipTimeLimits: localStorage.getItem('role') === 'affiliate'
                })
                : []
        );
        setPhModalOpen(true);
    };

    // Sulgeb Payment Holiday modali
    const handleClosePhModal = () => {
        setPhModalOpen(false);
        setPhContract(null);
        setPhEditingHoliday(null);
        setPhData({ startDate: '', endDate: '', reason: '' });
        setPhValidationErrors([]);
    };

    // Validate payment holiday dates
    const validatePaymentHolidayDates = (startDate, endDate, contract, options = {}) => {
        const errors = [];
        const skipTimeLimits = Boolean(options.skipTimeLimits);
        
        if (!startDate || !endDate) {
            return errors; // Don't validate if dates not set yet
        }

        const start = new Date(startDate);
        const end = new Date(endDate);

        // End date must be after start date
        if (end < start) {
            errors.push('End date must be after start date');
        }

        if (skipTimeLimits) {
            return errors;
        }

        // Minimum period check
        const durationDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        const minWeeks = contract?.affiliate?.minimumPaymentHolidayWeeks || 4;
        const minDays = minWeeks * 7;

        if (durationDays < minDays) {
            errors.push(
                `Period must be at least ${minWeeks} week(s) (${minDays} days). Selected: ${durationDays} day(s)`
            );
        }

        // Contract validity check
        if (contract?.validUntil) {
            const contractEnd = new Date(contract.validUntil);
            if (start > contractEnd) {
                errors.push('Cannot start after contract end date');
            }
            
            if (end > contractEnd) {
                errors.push(
                    `⚠️ Note: End date will be adjusted to contract end date (${formatDate(contractEnd)})`
                );
            }
        }

        return errors;
    };

    // Show success message with dialog
    const showSuccessMessage = (message) => {
        setDialogMessage(message);
        setSuccessDialogOpen(true);
    };

// Show error message
    const showErrorMessage = (message) => {
        setDialogMessage(message);
        setErrorDialogOpen(true);
    };

// Show confirmation dialog
    const showConfirmDialog = (title, message, onConfirm) => {
        setConfirmDialogTitle(title);
        setConfirmDialogMessage(message);
        setConfirmAction(() => onConfirm);
        setConfirmDialogOpen(true);
    };
    // Väljade muutmise handler
    const handlePhDataChange = (e) => {
        const { name, value } = e.target;
        const newData = { ...phData, [name]: value };
        setPhData(newData);
        
        // Validate if both dates are set
        if (name === 'startDate' || name === 'endDate') {
            const errors = validatePaymentHolidayDates(
                newData.startDate, 
                newData.endDate, 
                phContract,
                { skipTimeLimits: Boolean(phEditingHoliday) && localStorage.getItem('role') === 'affiliate' }
            );
            setPhValidationErrors(errors);
        }
    };

    // "Save" nupp modalis
    const handlePhSave = async () => {
        if (!phContract) return;

        // Validate required fields
        if (!phData.startDate || !phData.endDate) {
            showErrorMessage('Please select both start and end dates');
            return;
        }

        // Check for validation errors (but allow warnings starting with ⚠️)
        const blockingErrors = phValidationErrors.filter(err => !err.startsWith('⚠️'));
        if (blockingErrors.length > 0) {
            showErrorMessage(blockingErrors.join('\n'));
            return;
        }

        try {
            if (phEditingHoliday) {
                const result = await updatePaymentHoliday(phEditingHoliday.id, {
                    startDate: phData.startDate,
                    endDate: phData.endDate,
                    reason: phData.reason
                });

                if (result && result.success) {
                    let message = 'Payment holiday dates updated successfully!';
                    if (result.wasAdjusted) {
                        message += ' Note: End date was adjusted to contract end date.';
                    }
                    showSuccessMessage(message);
                } else {
                    const errorMsg = result?.errors?.join('\n') || result?.error || 'Error updating payment holiday dates!';
                    showErrorMessage(errorMsg);
                }
            } else {
                // Call new date-range API
                const result = await createPaymentHolidayDateRange(
                    phContract.id,
                    phData.startDate,
                    phData.endDate,
                    phData.reason
                );

                if (result && result.success) {
                    let message = 'Payment holiday request created successfully!';
                    if (result.wasAdjusted) {
                        message += ' Note: End date was adjusted to contract end date.';
                    }
                    showSuccessMessage(message);

                    // Notify affiliate
                    const getAffiliateData = await getAffiliateById(phContract.affiliateId);

                    const subject = 'Payment Holiday Request';
                    const startDate = formatDate(result?.data?.startDate || phData.startDate);
                    const selectedEndDate = formatDate(phData.endDate);
                    const approvedEndDate = formatDate(result?.data?.endDate || phData.endDate);
                    const contractEndDate = formatDate(phContract?.validUntil);
                    const adjustmentExplanation = result?.wasAdjusted
                        ? `<p><strong>Adjustment note:</strong> Contract ends on <strong>${contractEndDate}</strong>, but user selected <strong>${selectedEndDate}</strong>. Payment holiday end date was adjusted to <strong>${approvedEndDate}</strong>.</p>`
                        : '';
                    const body = `
<p>You have received a payment holiday request from <strong>${user.fullName}</strong>.</p>

<p><strong>Period:</strong> ${startDate} - ${approvedEndDate}</p>
${adjustmentExplanation}
${phData.reason ? `<p><strong>Reason:</strong> ${phData.reason}</p>` : ''}

<p>Go to Irontrack page -> My affiliate -> Contracts and choose <strong>${user.fullName}</strong> contract to approve or decline this request.</p>`;

                    const senderEmail = user.email;
                    const affiliateEmail = getAffiliateData.email;
                    await sendMessageToAffiliate(senderEmail, affiliateEmail, subject, body);
                } else {
                    const errorMsg = result?.errors?.join('\n') || result?.error || 'Error creating payment holiday request!';
                    showErrorMessage(errorMsg);
                }
            }
        } catch (error) {
            console.error('Error saving payment holiday:', error);
            showErrorMessage(phEditingHoliday
                ? 'Error updating payment holiday dates!'
                : 'Error creating payment holiday request!');
        }

        handleClosePhModal();
        await loadContracts();
    };

    const handleClosePhFollowUpModal = () => {
        setPhFollowUpModalOpen(false);
        setPhFollowUpData(null);
        setReturnAsCredit('yes');
        setDeductHolidayFee('no');
        setSendNewPaymentLinkChoice('no');
        setSendNextPeriodPaymentLinkChoice('no');
        setProcessingPhFollowUp(false);
    };

    useEffect(() => {
        // Keep state consistent: if credit is not returned, fee deduction is irrelevant.
        if (returnAsCredit === 'no' && deductHolidayFee !== 'no') {
            setDeductHolidayFee('no');
        }
    }, [returnAsCredit, deductHolidayFee]);

    useEffect(() => {
        // If holiday extends beyond known payment period(s), do not offer fee deduction choice.
        if (phFollowUpData?.skipFeeDeductionQuestion && deductHolidayFee !== 'no') {
            setDeductHolidayFee('no');
        }
    }, [phFollowUpData, deductHolidayFee]);

    const handleConfirmPhFollowUp = async () => {
        if (!phFollowUpData) {
            handleClosePhFollowUpModal();
            return;
        }

        setProcessingPhFollowUp(true);
        const actionsDone = [];

        try {
            const canDeductFeeInModal = !phFollowUpData?.skipFeeDeductionQuestion;
            const shouldReturnCredit = phFollowUpData.hasPaidInPeriod && returnAsCredit === 'yes';
            const finalCreditToReturn = shouldReturnCredit
                ? (canDeductFeeInModal && deductHolidayFee === 'yes'
                    ? Math.max(0, phFollowUpData.refundableBaseAmount - phFollowUpData.holidayFee)
                    : phFollowUpData.refundableBaseAmount)
                : 0;

            if (phFollowUpData.hasPaidInPeriod && returnAsCredit === 'yes') {
                if (finalCreditToReturn > 0) {
                    const creditDescription = `Payment holiday credit (${formatDate(phFollowUpData.periodStart)} - ${formatDate(phFollowUpData.periodEnd)})`;
                    await addCredit(
                        phFollowUpData.contract.userId,
                        phFollowUpData.contract.affiliateId,
                        parseFloat(finalCreditToReturn.toFixed(2)),
                        creditDescription
                    );
                    actionsDone.push(`Credit returned: ${formatCurrency(finalCreditToReturn, paymentCurrency)}`);
                } else {
                    actionsDone.push(`Credit return skipped (refund amount was ${formatCurrency(0, paymentCurrency)} after fee deduction)`);
                }
            } else if (phFollowUpData.hasPaidInPeriod && returnAsCredit === 'no') {
                actionsDone.push('No credit was returned (as selected)');
            }

            if (phFollowUpData.hasPendingInPeriod && sendNewPaymentLinkChoice === 'yes') {
                const linkResult = await sendPaymentLink(
                    phFollowUpData.contract.id,
                    'current',
                    {
                        start: (phFollowUpData.currentPendingPeriod?.start || phFollowUpData.periodStart).toISOString(),
                        end: (phFollowUpData.currentPendingPeriod?.end || phFollowUpData.periodEnd).toISOString()
                    }
                );

                if (!linkResult?.success) {
                    throw new Error(linkResult?.message || 'Failed to send a new payment link');
                }

                actionsDone.push('New payment link sent for the payment holiday period');
            }

            if (phFollowUpData.hasPendingInNextPeriod && sendNextPeriodPaymentLinkChoice === 'yes') {
                const nextPeriodLinkResult = await sendPaymentLink(
                    phFollowUpData.contract.id,
                    'next',
                    {
                        start: phFollowUpData.nextPendingPeriod.start.toISOString(),
                        end: phFollowUpData.nextPendingPeriod.end.toISOString()
                    }
                );

                if (!nextPeriodLinkResult?.success) {
                    throw new Error(nextPeriodLinkResult?.message || 'Failed to send a new payment link for the next period');
                }

                actionsDone.push('New payment link sent for the next period');
            }

            const followUpMessage = actionsDone.length > 0
                ? `Payment holiday confirmed. ${actionsDone.join('. ')}.`
                : 'Payment holiday confirmed. No additional action selected.';

            showSuccessMessage(followUpMessage);
            handleClosePhFollowUpModal();
            await loadContracts();
        } catch (error) {
            console.error('Error processing payment holiday follow-up:', error);
            showErrorMessage(error?.message || 'Error while processing follow-up actions');
        } finally {
            setProcessingPhFollowUp(false);
        }
    };

    // PaymentHoliday uuendamise handler (kui affiliate klikkab Approved/Declined)
    const handleUpdatePhStatus = async (ph, contract, newStatus) => {
        setProcessingPhActionId(ph.id);
        try {
            const result = await updatePaymentHoliday(ph.id, { accepted: newStatus });
            if (result && result.success) {
                if (newStatus !== 'approved') {
                    showSuccessMessage(`Payment holiday set to "${newStatus}" successfully!`);
                    await loadContracts();
                    return;
                }

                const periodStart = normalizeDateOnly(ph.startDate);
                const periodEnd = normalizeDateOnly(ph.endDate);

                if (!periodStart || !periodEnd) {
                    showSuccessMessage('Payment holiday request confirmed.');
                    await loadContracts();
                    return;
                }

                const userTransactions = await getUserTransactions(contract.affiliateId, contract.userId);
                const contractTransactions = Array.isArray(userTransactions)
                    ? userTransactions.filter((tx) => tx.contractId === contract.id)
                    : [];

                const inPeriodTransactions = contractTransactions.filter((tx) => {
                    const txStart = normalizeDateOnly(tx.paymentMetadata?.periodStart);
                    const txEnd = normalizeDateOnly(tx.paymentMetadata?.periodEnd);
                    return periodsOverlap(txStart, txEnd, periodStart, periodEnd);
                });

                const paidStatuses = new Set(['success', 'paid', 'completed']);
                const paidTransactions = inPeriodTransactions.filter((tx) =>
                    paidStatuses.has(String(tx.status || '').toLowerCase())
                );
                const pendingTransactions = inPeriodTransactions.filter((tx) =>
                    String(tx.status || '').toLowerCase() === 'pending'
                );
                const overlappingPeriodsMap = new Map();
                inPeriodTransactions.forEach((tx) => {
                    const txStart = normalizeDateOnly(tx.paymentMetadata?.periodStart);
                    const txEnd = normalizeDateOnly(tx.paymentMetadata?.periodEnd);
                    if (!txStart || !txEnd) return;
                    const key = `${txStart.toISOString()}_${txEnd.toISOString()}`;
                    if (!overlappingPeriodsMap.has(key)) {
                        overlappingPeriodsMap.set(key, { start: txStart, end: txEnd });
                    }
                });
                const overlappingPeriods = Array.from(overlappingPeriodsMap.values()).sort((a, b) => a.start - b.start);
                const currentAffectedPeriod = overlappingPeriods.find((period) =>
                    period.start <= periodStart && period.end >= periodStart
                ) || overlappingPeriods[0] || null;

                const pendingPeriodsMap = new Map();
                pendingTransactions.forEach((tx) => {
                    const txStart = normalizeDateOnly(tx.paymentMetadata?.periodStart);
                    const txEnd = normalizeDateOnly(tx.paymentMetadata?.periodEnd);
                    if (!txStart || !txEnd) return;
                    const key = `${txStart.toISOString()}_${txEnd.toISOString()}`;
                    if (!pendingPeriodsMap.has(key)) {
                        pendingPeriodsMap.set(key, { start: txStart, end: txEnd });
                    }
                });
                const pendingPeriods = Array.from(pendingPeriodsMap.values()).sort((a, b) => a.start - b.start);
                const currentPendingPeriod = pendingPeriods.find((period) =>
                    currentAffectedPeriod &&
                    period.start.getTime() === currentAffectedPeriod.start.getTime() &&
                    period.end.getTime() === currentAffectedPeriod.end.getTime()
                ) || pendingPeriods.find((period) =>
                    period.start <= periodStart && period.end >= periodStart
                ) || null;
                const nextPendingPeriod = currentAffectedPeriod
                    ? pendingPeriods.find((period) => period.start > currentAffectedPeriod.end)
                    : (currentPendingPeriod
                        ? pendingPeriods.find((period) => period.start > currentPendingPeriod.end)
                        : null);

                const contractMonthlyAmount = parseAmount(contract.paymentAmount);
                const refundableBaseAmount = paidTransactions.reduce((sum, tx) => {
                    const txStart = normalizeDateOnly(tx.paymentMetadata?.periodStart);
                    const txEnd = normalizeDateOnly(tx.paymentMetadata?.periodEnd);
                    const overlapDays = calculateOverlappingDays(txStart, txEnd, periodStart, periodEnd);
                    const periodDays = calculateOverlappingDays(txStart, txEnd, txStart, txEnd);

                    if (overlapDays <= 0 || periodDays <= 0) {
                        return sum;
                    }

                    const monthlyBase = contractMonthlyAmount > 0 ? contractMonthlyAmount : parseAmount(tx.amount);
                    return sum + ((monthlyBase / periodDays) * overlapDays);
                }, 0);
                const holidayDays = Math.ceil((periodEnd - periodStart) / (1000 * 60 * 60 * 24)) + 1;
                const holidayFeePerMonth = parseAmount(contract.affiliate?.paymentHolidayFee);
                // Deduct payment holiday fee only once per approved request.
                const totalHolidayFee = holidayFeePerMonth > 0 ? holidayFeePerMonth : 0;
                const exceedsCurrentMetadataPeriod = Boolean(
                    currentAffectedPeriod && periodEnd > currentAffectedPeriod.end
                );
                const skipFeeDeductionQuestion = exceedsCurrentMetadataPeriod && holidayDays < 31;

                if (paidTransactions.length === 0 && pendingTransactions.length === 0) {
                    showSuccessMessage('Payment holiday request confirmed.');
                    await loadContracts();
                    return;
                }

                setPhFollowUpData({
                    contract,
                    paymentHoliday: ph,
                    periodStart,
                    periodEnd,
                    hasPaidInPeriod: paidTransactions.length > 0,
                    hasPendingInPeriod: Boolean(currentPendingPeriod),
                    currentPendingPeriod,
                    hasPendingInNextPeriod: Boolean(nextPendingPeriod),
                    nextPendingPeriod,
                    refundableBaseAmount: parseFloat(refundableBaseAmount.toFixed(2)),
                    holidayFee: parseFloat(totalHolidayFee.toFixed(2)),
                    skipFeeDeductionQuestion,
                    exceedsCurrentMetadataPeriod,
                    holidayDays
                });
                setReturnAsCredit('yes');
                setDeductHolidayFee('no');
                setSendNewPaymentLinkChoice('no');
                setSendNextPeriodPaymentLinkChoice('no');
                setPhFollowUpModalOpen(true);
            } else {
                showErrorMessage('Error updating payment holiday status!');
            }
        } catch (error) {
            console.error('Error updating payment holiday:', error);
            alert('Error updating payment holiday!');
        } finally {
            setProcessingPhActionId(null);
        }
    };

    // Funktsioon dialoogi avamiseks
    const handleOpenDeactivateDialog = (contract) => {
        setContractToDeactivate(contract);
        setOpenDeactivateDialog(true);
    };

    // Funktsioon dialoogi sulgemiseks ja deaktiveerimiseks
    const handleConfirmDeactivate = () => {
        if (newEndDate && contractToDeactivate) {
            handleDeActivate({ ...contractToDeactivate, endDate: newEndDate, affiliateId });
            showSuccessMessage("Contract end date updated successfully!");
        }
        setOpenDeactivateDialog(false);
        setNewEndDate("");
        setContractToDeactivate(null);
    };

    // Funktsioon dialoogi tühistamiseks
    const handleCancelDeactivate = () => {
        setOpenDeactivateDialog(false);
        setNewEndDate("");
        setContractToDeactivate(null);
    };

    const handleDeActivate = async (contractData) => {
        const payload = {
            affiliateId: contractData.affiliateId,
            userId: contractData.userId,
            endDate: contractData.endDate, // Võtame endDate otse objektist
            action: 'change end date',
        };
        await updateContract(contractData.id, payload);
        await loadContracts();
    };

    const role = localStorage.getItem('role');
    const canCreateContract =
        role === 'affiliate' && localStorage.getItem('pricingPlan') === 'premium';

    const handleCreateContract = () => {
        navigate('/my-affiliate', {
            state: {
                activeComponent: 'contracts',
                createContractForUser: {
                    id: user.id,
                    fullName: user.fullName,
                    email: user.email,
                },
            },
        });
    };

    const formatDate = (dateString, options = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }) => {
        if (!dateString) return 'N/A';
        return new Intl.DateTimeFormat('et-EE', options).format(new Date(dateString));
    };

    const normalizeDateOnly = (dateInput) => {
        if (!dateInput) return null;
        const date = new Date(dateInput);
        if (Number.isNaN(date.getTime())) return null;
        date.setHours(0, 0, 0, 0);
        return date;
    };

    const periodsOverlap = (startA, endA, startB, endB) => {
        if (!startA || !endA || !startB || !endB) return false;
        return startA <= endB && endA >= startB;
    };

    const parseAmount = (value) => {
        const amount = Number(value);
        return Number.isFinite(amount) ? amount : 0;
    };

    const calculateOverlappingDays = (rangeStart, rangeEnd, periodStart, periodEnd) => {
        if (!rangeStart || !rangeEnd || !periodStart || !periodEnd) return 0;
        const overlapStart = rangeStart > periodStart ? rangeStart : periodStart;
        const overlapEnd = rangeEnd < periodEnd ? rangeEnd : periodEnd;
        if (overlapEnd < overlapStart) return 0;
        return Math.ceil((overlapEnd - overlapStart) / (1000 * 60 * 60 * 24)) + 1;
    };

    const getVisiblePaymentHolidays = (holidays) =>
        (holidays || []).filter((ph) => !(ph.month && ph.month.trim() !== ''));

    const formatPaymentHolidayPeriod = (ph) => {
        if (ph.startDate && ph.endDate) {
            return `${formatDate(ph.startDate)} - ${formatDate(ph.endDate)}`;
        }

        return 'N/A';
    };

    // Calculate payment periods based on paymentDay
    const calculatePaymentPeriods = (paymentDay) => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();

        // Calculate current period
        const currentPeriodStart = new Date(currentYear, currentMonth, paymentDay);
        const currentPeriodEnd = new Date(currentYear, currentMonth + 1, paymentDay - 1);

        // If today is before the payment day, we're in the previous period
        if (today.getDate() < paymentDay) {
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
            currentPeriodEnd.setMonth(currentPeriodEnd.getMonth() - 1);
        }

        // Calculate previous period
        const previousPeriodStart = new Date(currentPeriodStart);
        previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
        const previousPeriodEnd = new Date(currentPeriodStart);
        previousPeriodEnd.setDate(previousPeriodEnd.getDate() - 1);

        // Calculate next period
        const nextPeriodStart = new Date(currentPeriodEnd);
        nextPeriodStart.setDate(nextPeriodStart.getDate() + 1);
        const nextPeriodEnd = new Date(nextPeriodStart);
        nextPeriodEnd.setMonth(nextPeriodEnd.getMonth() + 1);
        nextPeriodEnd.setDate(nextPeriodEnd.getDate() - 1);

        return {
            previous: {
                start: previousPeriodStart,
                end: previousPeriodEnd,
                label: `${formatDate(previousPeriodStart, { day: '2-digit', month: '2-digit' })} - ${formatDate(previousPeriodEnd)}`
            },
            current: {
                start: currentPeriodStart,
                end: currentPeriodEnd,
                label: `${formatDate(currentPeriodStart, { day: '2-digit', month: '2-digit' })} - ${formatDate(currentPeriodEnd)}`
            },
            next: {
                start: nextPeriodStart,
                end: nextPeriodEnd,
                label: `${formatDate(nextPeriodStart, { day: '2-digit', month: '2-digit' })} - ${formatDate(nextPeriodEnd)}`
            }
        };
    };

    // Handler to open send payment link confirmation dialog
    const handleOpenSendLinkDialog = (contract) => {
        setContractToSendLink(contract);
        setSelectedMonth('current'); // Reset to default
        
        // Calculate payment periods based on contract's paymentDay
        if (contract.paymentDay) {
            const periods = calculatePaymentPeriods(contract.paymentDay);
            setPaymentPeriods(periods);
        }
        
        setSendLinkDialogOpen(true);
    };

// Handler to send payment link
    const handleSendPaymentLink = async () => {
        if (!contractToSendLink) return;

        setSendingPaymentLink(true);

        try {
            const selectedPeriod = paymentPeriods[selectedMonth];
            const periodLabel = selectedPeriod ? selectedPeriod.label : selectedMonth;
            
            const result = await sendPaymentLink(
                contractToSendLink.id, 
                selectedMonth,
                selectedPeriod ? {
                    start: selectedPeriod.start.toISOString(),
                    end: selectedPeriod.end.toISOString()
                } : null
            );

            if (result.success) {
                showSuccessMessage(`Payment link sent successfully! Previous pending transaction has been cancelled and a new payment link has been sent to the user for period ${periodLabel}.`);
                // Reload contracts to refresh the data
                await loadContracts();
            } else {
                showErrorMessage(result.message || "Failed to send payment link");
            }
        } catch (error) {
            console.error("Error sending payment link:", error);
            showErrorMessage("An error occurred while sending the payment link");
        } finally {
            setSendingPaymentLink(false);
            setSendLinkDialogOpen(false);
            setContractToSendLink(null);
        }
    };

    const refundableAmountWithCurrentFeeSetting = phFollowUpData
        ? Math.max(
            0,
            returnAsCredit === 'yes'
                ? phFollowUpData.refundableBaseAmount - (
                    !phFollowUpData.skipFeeDeductionQuestion && deductHolidayFee === 'yes'
                        ? phFollowUpData.holidayFee
                        : 0
                )
                : 0
        )
        : 0;
    const terminationMinimum = getTerminationMinimumDetails(terminationContract);

    return (
        <Box>
            <Box
                sx={{
                    mb: 2,
                    ml: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    My Contracts
                </Typography>
                {canCreateContract && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateContract}
                    >
                        Create Contract
                    </Button>
                )}
            </Box>
            {/* Success message - add this after your "My Contracts" Typography */}
            {successMessage && (
                <Box sx={{ mb: 2, p: 1, bgcolor: '#e6f7e6', borderRadius: 1 }}>
                    <Typography color="success">{successMessage}</Typography>
                </Box>
            )}
            {contracts.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography>No contracts found.</Typography>
                </Paper>
            ) : (
                <Card elevation={2} sx={{ overflow: 'hidden', pl: 0, pr: 0}}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>

                                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Affiliate Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}> </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {contracts.map((contract) => {
                                const isExpanded = expandedRows[contract.id] || false;
                                const isSent = contract.status === 'Waiting for acceptance';
                                return (
                                    <React.Fragment key={contract.id}>
                                        <TableRow
                                            hover
                                            onClick={() => toggleRow(contract.id)}
                                            sx={{ cursor: 'pointer' }}
                                        >

                                            <TableCell>
                                                {formatDate(contract.createdAt)}
                                            </TableCell>
                                            <TableCell>{contract.affiliate.name}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={contract.status}
                                                    color={
                                                        contract.status === 'accepted' ? 'success' :
                                                            contract.status === 'Waiting for acceptance' ? 'warning' :
                                                                contract.status === 'terminated' ? 'error' :
                                                                    'primary'
                                                    }
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                { contract.status === 'accepted' && role === 'affiliate' && (
                                                    <IconButton
                                                        color="secondary"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenDeactivateDialog(contract);
                                                        }}
                                                        aria-label="end date"
                                                    >
                                                        <CalendarMonthIcon />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>

                                        <TableRow>
                                            <TableCell colSpan={6} style={{ paddingBottom: 0, paddingTop: 0, pl: 0, width: {xs: '100vw' }}}>
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ margin: 2 }}>
                                                        <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 'bold', mb: 3 }}>
                                                            Contract Details
                                                        </Typography>
                                                        {/* Send Payment Link Button - Only for affiliates and accepted contracts */}
                                                        {contract.status === 'accepted' && role === 'affiliate' && (
                                                            <Box sx={{
                                                                mb: 3,
                                                                p: 2,
                                                                bgcolor: '#f8f9fa',
                                                                borderRadius: 1,
                                                                border: '1px solid #e0e0e0',
                                                                textAlign: 'center'
                                                            }}>
                                                                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                                                    Send a new payment link to the user. This will cancel any existing pending payment and create a fresh payment link.
                                                                </Typography>

                                                                <Button
                                                                    variant="contained"
                                                                    color="primary"
                                                                    startIcon={<SendIcon />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleOpenSendLinkDialog(contract);
                                                                    }}
                                                                    sx={{ minWidth: 180 }}
                                                                >
                                                                    Send Payment Link
                                                                </Button>
                                                            </Box>
                                                        )}
                                                        <Grid container spacing={3}>
                                                            {/* Payment Holidays Card */}
                                                            <Grid item xs={12}>
                                                                <Card elevation={1}>
                                                                    <CardContent>
                                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50' }}>
                                                                            <CalendarMonthIcon sx={{ mr: 1 }} />
                                                                            Payment Holidays
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />

                                                                        {getVisiblePaymentHolidays(contract.paymentHolidays).length > 0 ? (
                                                                            <Table>
                                                                                <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
                                                                                    <TableRow>
                                                                                        <TableCell sx={{ fontWeight: 'bold' }}>Period</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold' }}>Reason</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                                                                    </TableRow>
                                                                                </TableHead>
                                                                                <TableBody>
                                                                                    {getVisiblePaymentHolidays(contract.paymentHolidays).map((ph) => (
                                                                                        <TableRow key={ph.id}>
                                                                                            <TableCell>{formatPaymentHolidayPeriod(ph)}</TableCell>
                                                                                            <TableCell>{ph.reason || '-'}</TableCell>
                                                                                            <TableCell>
                                                                                                <Chip
                                                                                                    label={ph.accepted}
                                                                                                    color={
                                                                                                        ph.accepted === 'approved' ? 'success' :
                                                                                                            ph.accepted === 'declined' ? 'error' :
                                                                                                                'warning'
                                                                                                    }
                                                                                                    size="small"
                                                                                                />
                                                                                            </TableCell>
                                                                                            <TableCell>
                                                                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                                                                    {/* Kui role===affiliate ja ph.accepted==='pending',
            siis kuvame rohelise & punase nupu */}
                                                                                                    {role === 'affiliate' && ph.accepted === 'pending' && (
                                                                                                        <>
                                                                                                            <IconButton
                                                                                                                color="success"
                                                                                                                onClick={(e) => {
                                                                                                                    e.stopPropagation();
                                                                                                                    handleUpdatePhStatus(ph, contract, 'approved');
                                                                                                                }}
                                                                                                                disabled={processingPhActionId === ph.id}
                                                                                                            >
                                                                                                                {processingPhActionId === ph.id ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                                                                                                            </IconButton>
                                                                                                            <IconButton
                                                                                                                color="error"
                                                                                                                onClick={(e) => {
                                                                                                                    e.stopPropagation();
                                                                                                                    handleUpdatePhStatus(ph, contract, 'declined');
                                                                                                                }}
                                                                                                                disabled={processingPhActionId === ph.id}
                                                                                                            >
                                                                                                                <CloseIcon />
                                                                                                            </IconButton>
                                                                                                        </>
                                                                                                    )}
                                                                                                    {((role === 'affiliate') || (role === 'regular' && ph.accepted === 'pending')) && (
                                                                                                        <IconButton
                                                                                                            color="primary"
                                                                                                            onClick={(e) => {
                                                                                                                e.stopPropagation();
                                                                                                                handleOpenPhModal(contract, ph);
                                                                                                            }}
                                                                                                            aria-label="edit payment holiday dates"
                                                                                                            title="Edit dates"
                                                                                                        >
                                                                                                            <EditIcon />
                                                                                                        </IconButton>
                                                                                                    )}
                                                                                                    {role === 'affiliate' && (
                                                                                                        <IconButton
                                                                                                            color="error"
                                                                                                            onClick={(e) => {
                                                                                                                e.stopPropagation();
                                                                                                                handleDeletePaymentHoliday(ph.id);
                                                                                                            }}
                                                                                                            aria-label="delete payment holiday"
                                                                                                            sx={{
                                                                                                                color: '#d32f2f',
                                                                                                                '&:hover': {
                                                                                                                    backgroundColor: '#ffebee',
                                                                                                                    color: '#c62828'
                                                                                                                }
                                                                                                            }}
                                                                                                        >
                                                                                                            <DeleteIcon />
                                                                                                        </IconButton>
                                                                                                    )}
                                                                                                </Box>
                                                                                            </TableCell>
                                                                                        </TableRow>
                                                                                    ))}
                                                                                </TableBody>
                                                                            </Table>
                                                                        ) : (
                                                                            <Typography variant="body1" sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                                                                                No payment holidays found for this contract
                                                                            </Typography>
                                                                        )}

                                                                        {/* PaymentHoliday nupp (Request) — kui leping on accepted ja kasutaja on 'regular' */}
                                                                        {contract.status === 'accepted' && role === 'regular' && (
                                                                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                                                                <Button
                                                                                    variant="contained"
                                                                                    color="primary"
                                                                                    startIcon={<EventIcon />}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleOpenPhModal(contract);
                                                                                    }}
                                                                                >
                                                                                    Request Payment Holiday
                                                                                </Button>
                                                                            </Box>
                                                                        )}
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>

                                                            {/* End date requests Card */}
                                                            <Grid item xs={12}>
                                                                <Card elevation={1}>
                                                                    <CardContent>
                                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50' }}>
                                                                            <EventBusyIcon sx={{ mr: 1 }} />
                                                                            End Date Requests
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />

                                                                        {contract.terminationRequests && contract.terminationRequests.length > 0 ? (
                                                                            <Table>
                                                                                <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
                                                                                    <TableRow>
                                                                                        <TableCell sx={{ fontWeight: 'bold' }}>Requested End Date</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold' }}>Reason</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                                                                    </TableRow>
                                                                                </TableHead>
                                                                                <TableBody>
                                                                                    {contract.terminationRequests.map((tr) => (
                                                                                        <TableRow key={tr.id}>
                                                                                            <TableCell>{formatDate(tr.requestedEndDate)}</TableCell>
                                                                                            <TableCell>{tr.reason || '—'}</TableCell>
                                                                                            <TableCell>
                                                                                                <Chip
                                                                                                    label={tr.accepted}
                                                                                                    color={
                                                                                                        tr.accepted === 'approved' ? 'success' :
                                                                                                            tr.accepted === 'declined' ? 'error' :
                                                                                                                'warning'
                                                                                                    }
                                                                                                    size="small"
                                                                                                />
                                                                                            </TableCell>
                                                                                            <TableCell>
                                                                                                {role === 'affiliate' && tr.accepted === 'pending' && (
                                                                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                                                                        <IconButton
                                                                                                            color="success"
                                                                                                            onClick={(e) => {
                                                                                                                e.stopPropagation();
                                                                                                                handleUpdateTerminationStatus(tr, contract, 'approved');
                                                                                                            }}
                                                                                                            disabled={processingTerminationActionId === tr.id}
                                                                                                        >
                                                                                                            {processingTerminationActionId === tr.id
                                                                                                                ? <CircularProgress size={20} color="inherit" />
                                                                                                                : <CheckIcon />}
                                                                                                        </IconButton>
                                                                                                        <IconButton
                                                                                                            color="error"
                                                                                                            onClick={(e) => {
                                                                                                                e.stopPropagation();
                                                                                                                handleUpdateTerminationStatus(tr, contract, 'declined');
                                                                                                            }}
                                                                                                            disabled={processingTerminationActionId === tr.id}
                                                                                                        >
                                                                                                            <CloseIcon />
                                                                                                        </IconButton>
                                                                                                    </Box>
                                                                                                )}
                                                                                            </TableCell>
                                                                                        </TableRow>
                                                                                    ))}
                                                                                </TableBody>
                                                                            </Table>
                                                                        ) : (
                                                                            <Typography variant="body1" sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                                                                                No end date requests found for this contract
                                                                            </Typography>
                                                                        )}

                                                                        {contract.status === 'accepted' && role === 'regular' && (
                                                                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                                                                <Button
                                                                                    variant="contained"
                                                                                    color="primary"
                                                                                    startIcon={<EventBusyIcon />}
                                                                                    disabled={(contract.terminationRequests || []).some((tr) => tr.accepted === 'pending')}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleOpenTerminationModal(contract);
                                                                                    }}
                                                                                >
                                                                                    Request Contract End
                                                                                </Button>
                                                                            </Box>
                                                                        )}
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>

                                                            {/* Payment Information Card */}
                                                            {/* Payment Information Card */}
                                                            <Grid item xs={12} md={6}>
                                                                <Card elevation={1} sx={{ height: '100%' }}>
                                                                    <CardContent>
                                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50' }}>
                                                                            <PaymentIcon sx={{ mr: 1 }} />
                                                                            Payment Information
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />

                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Payment Type:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {contract.paymentType || 'N/A'}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Payment Amount:
                                                                                </Typography>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                                                    {formatMoney(contract.paymentAmount, contract)}
                                                                                </Typography>
                                                                            </Box>

                                                                            {contract.isFirstPayment && (
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                        First Payment Amount:
                                                                                    </Typography>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                                                        {formatMoney(contract.firstPaymentAmount, contract)}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}

                                                                            {contract.discountMonths > 0 && (
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                        Discount payments left:
                                                                                    </Typography>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                                                        {contract.discountMonths}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}

                                                                            {contract.discountMonths > 0 && (
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                        Discount Percentage:
                                                                                    </Typography>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                                                        {contract.discountPercent} %
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}

                                                                            {contract.discountMonths > 0 && (
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                        Discount Price:
                                                                                    </Typography>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                                                        {formatMoney(contract.paymentAmount * ((100 - contract.discountPercent) / 100), contract)}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}

                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Payment Interval:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {contract.paymentInterval || 'N/A'}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Payment Day:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {contract.paymentDay || 'N/A'}
                                                                                </Typography>
                                                                            </Box>



                                                                            {/* Prorated Payment Calculation Section - only show when there's a monthly fee + prorated amount */}
                                                                            {contract.firstPaymentAmount && contract.paymentAmount && contract.isFirstPayment && (
                                                                                <Box sx={{ mt: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 2, bgcolor: '#f9f9f9' }}>
                                                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                                                        Prorated Payment Calculation:
                                                                                    </Typography>

                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                        <Typography variant="body2" sx={{ mr: 1, minWidth: 160 }}>
                                                                                            Regular Payment Amount:
                                                                                        </Typography>
                                                                                        <Typography variant="body2">
                                                                                            {formatMoney(contract.paymentAmount, contract)}
                                                                                        </Typography>
                                                                                    </Box>

                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                        <Typography variant="body2" sx={{ mr: 1, minWidth: 160 }}>
                                                                                            First Payment Amount:
                                                                                        </Typography>
                                                                                        <Typography variant="body2">
                                                                                            {formatMoney(contract.firstPaymentAmount, contract)}
                                                                                        </Typography>
                                                                                    </Box>

                                                                                    {/* Explanation in English */}
                                                                                    <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f0f7ff', borderRadius: 1, border: '1px solid #e3f2fd' }}>
                                                                                        <Typography variant="body2" color="text.secondary">
                                                                                            <strong>Explanation:</strong> The first payment is proportionally calculated because the contract starts in the middle of a billing period.
                                                                                            Calculated from {contract.startDate ? formatDate(contract.startDate) : ''} until the end of the period
                                                                                            (remaining days of a 30-day period).
                                                                                        </Typography>
                                                                                    </Box>
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>

                                                            {/* Contract Information Card */}
                                                            <Grid item xs={12} md={6}>
                                                                <Card elevation={1} sx={{ height: '100%' }}>
                                                                    <CardContent>
                                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50' }}>
                                                                            <DescriptionIcon sx={{ mr: 1 }} />
                                                                            Contract Information
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />

                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                            {/* Lisa family member info */}
                                                                            {contract.isFamilyMemberContract && contract.familyMember && (
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                        Family Member:
                                                                                    </Typography>
                                                                                    <Typography variant="body1">
                                                                                        {contract.familyMember.fullName}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Contract ID:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {contract.id}
                                                                                </Typography>
                                                                            </Box>

                                                                            {/* Training Types Section */}
                                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140, mt: 0.5 }}>
                                                                                    Training Types:
                                                                                </Typography>
                                                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                                                    {getTrainingTypesArray(contract.trainingType).length > 0 ? (
                                                                                        getTrainingTypesArray(contract.trainingType).map((type, index) => (
                                                                                            <Chip
                                                                                                key={index}
                                                                                                label={type}
                                                                                                color={getTrainingTypeColor(type)}
                                                                                                size="small"
                                                                                                icon={<FitnessCenterIcon />}
                                                                                                sx={{ mb: 0.5 }}
                                                                                            />
                                                                                        ))
                                                                                    ) : (
                                                                                        <Typography variant="body2" color="text.secondary">
                                                                                            No training types specified
                                                                                        </Typography>
                                                                                    )}
                                                                                </Stack>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Created Date:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {formatDate(contract.createdAt)}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Starting date:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {formatDate(contract.startDate)}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Valid Until:
                                                                                </Typography>
                                                                                <Typography variant="body1">
                                                                                    {formatDate(contract.validUntil)}
                                                                                </Typography>
                                                                            </Box>

                                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1, minWidth: 140 }}>
                                                                                    Status:
                                                                                </Typography>
                                                                                <Chip
                                                                                    label={contract.status}
                                                                                    color={
                                                                                        contract.status === 'accepted' ? 'success' :
                                                                                            contract.status === 'Waiting for acceptance' ? 'warning' :
                                                                                                contract.status === 'terminated' ? 'error' :
                                                                                                    'primary'
                                                                                    }
                                                                                    size="small"
                                                                                />
                                                                            </Box>
                                                                        </Box>
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>

                                                            {/* Price Changes Card (contract amendments) */}
                                                            {contract.priceAmendments && contract.priceAmendments.length > 0 && (
                                                                <Grid item xs={12}>
                                                                    <Card elevation={1}>
                                                                        <CardContent>
                                                                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50' }}>
                                                                                <PriceChangeIcon sx={{ mr: 1 }} />
                                                                                Price Changes
                                                                            </Typography>
                                                                            <Divider sx={{ mb: 2 }} />
                                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                                                These amendments to your contract were made by the club. You were notified by e-mail
                                                                                about each change. If you do not agree with a change, you can submit an end date request
                                                                                for this contract or contact the club.
                                                                            </Typography>
                                                                            <Table size="small">
                                                                                <TableHead>
                                                                                    <TableRow>
                                                                                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Old price</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">New price</TableCell>
                                                                                        <TableCell sx={{ fontWeight: 'bold' }}>Reason</TableCell>
                                                                                    </TableRow>
                                                                                </TableHead>
                                                                                <TableBody>
                                                                                    {contract.priceAmendments.map((pa) => (
                                                                                        <TableRow key={pa.id}>
                                                                                            <TableCell>{formatDate(pa.createdAt)}</TableCell>
                                                                                            <TableCell align="right">{formatMoney(pa.previousAmount, contract)}</TableCell>
                                                                                            <TableCell align="right">{formatMoney(pa.newAmount, contract)}</TableCell>
                                                                                            <TableCell>{pa.reason || '—'}</TableCell>
                                                                                        </TableRow>
                                                                                    ))}
                                                                                </TableBody>
                                                                            </Table>
                                                                        </CardContent>
                                                                    </Card>
                                                                </Grid>
                                                            )}

                                                            {/* Contract Content Card */}
                                                            <Grid item xs={12}>
                                                                <Card elevation={1}>
                                                                    <CardContent>
                                                                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50' }}>
                                                                            <DescriptionIcon sx={{ mr: 1 }} />
                                                                            Contract Content
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />

                                                                        <Box
                                                                            sx={{
                                                                                whiteSpace: 'pre-line',
                                                                                backgroundColor: '#f8f9fa',
                                                                                p: 2,
                                                                                borderRadius: 1,
                                                                                maxHeight: '300px',
                                                                                overflowY: 'auto'
                                                                            }}
                                                                        >
                                                                            {contract.content}
                                                                        </Box>

                                                                        {/* Accept Contract section */}
                                                                        {isSent && role === 'regular' && (
                                                                            <Box sx={{
                                                                                display: 'flex',
                                                                                flexDirection: 'column',
                                                                                gap: 2,
                                                                                mt: 3,
                                                                                p: 2,
                                                                                borderRadius: 1,
                                                                                border: '1px solid #e0e0e0',
                                                                                backgroundColor: '#f8f9fa'
                                                                            }}>
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                    <Checkbox
                                                                                        checked={!!acceptCheckbox[contract.id]}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                        onChange={(e) =>
                                                                                            handleCheckboxChange(
                                                                                                contract.id,
                                                                                                e.target.checked
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                    <Typography>
                                                                                        I have read and understand Terms and Conditions
                                                                                    </Typography>
                                                                                    <Button
                                                                                        variant="outlined"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            openTerms(contract);
                                                                                        }}
                                                                                        size="small"
                                                                                    >
                                                                                        Open Terms
                                                                                    </Button>
                                                                                </Box>

                                                                                <Button
                                                                                    variant="contained"
                                                                                    color="primary"
                                                                                    disabled={!acceptCheckbox[contract.id]}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleAccept(contract);
                                                                                    }}
                                                                                    sx={{ alignSelf: 'flex-start' }}
                                                                                >
                                                                                    Accept Contract
                                                                                </Button>
                                                                            </Box>
                                                                        )}
                                                                    </CardContent>
                                                                </Card>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Card>
            )}

            {/* Contract Terms modal */}
            {termsModalOpen && (
                <ContractTermsModal
                    open={termsModalOpen}
                    onClose={() => setTermsModalOpen(false)}
                    termsId={selectedContractTermsId}
                />
            )}

            {/* PaymentHoliday Modal - Date Range */}
            <Modal open={phModalOpen} onClose={handleClosePhModal}>
                <Card
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        minWidth: 400,
                        maxWidth: 600,
                        outline: 'none',
                        boxShadow: 24,
                    }}
                >
                    <CardContent>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50', mb: 2 }}>
                            <EventIcon sx={{ mr: 1 }} />
                            {phEditingHoliday ? 'Edit Payment Holiday' : 'Request Payment Holiday'}
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        {phEditingHoliday && (
                            <Paper
                                elevation={0}
                                sx={{
                                    mb: 2,
                                    p: 2,
                                    bgcolor: '#fff8e1',
                                    borderLeft: 4,
                                    borderColor: '#f9a825',
                                    borderRadius: 1
                                }}
                            >
                                <Typography variant="body2">
                                    {role === 'affiliate'
                                        ? `You can change the payment holiday dates ${phEditingHoliday.accepted === 'approved' ? 'after approval' : 'before approval'}. Changing dates does not automatically recalculate credits or payment links.`
                                        : 'You can change the dates until the gym approves this request.'}
                                </Typography>
                            </Paper>
                        )}

                        {/* Info banner about minimum period and payment holiday fee */}
                        {phContract && !(phEditingHoliday && role === 'affiliate') && (
                            <>
                                <Paper 
                                    elevation={0} 
                                    sx={{ 
                                        mb: 2, 
                                        p: 2, 
                                        bgcolor: 'info.lighter', 
                                        borderLeft: 4, 
                                        borderColor: 'info.main',
                                        borderRadius: 1 
                                    }}
                                >
                                    <Typography variant="body2" color="info.dark">
                                        ℹ️ Minimum period: {phContract.affiliate?.minimumPaymentHolidayWeeks || 4} weeks ({(phContract.affiliate?.minimumPaymentHolidayWeeks || 4) * 7} days)
                                    </Typography>
                                </Paper>
                                
                                {phContract.affiliate?.paymentHolidayFee && (
                                    <Paper 
                                        elevation={0} 
                                        sx={{ 
                                            mb: 3, 
                                            p: 2, 
                                            bgcolor: '#fff3e0', 
                                            borderLeft: 4, 
                                            borderColor: '#ff9800',
                                            borderRadius: 1 
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight="bold" color="#e65100">
                                            Payment Holiday Fee: {formatMoney(phContract.affiliate.paymentHolidayFee, phContract)} per month (31 days)
                                        </Typography>
                                    </Paper>
                                )}
                            </>
                        )}

                        {/* Start Date */}
                            <TextField
                            label="Start Date"
                            name="startDate"
                            type="date"
                            value={phData.startDate}
                                onChange={handlePhDataChange}
                                fullWidth
                            margin="normal"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={phEditingHoliday && role === 'affiliate' ? undefined : {
                                max: phContract?.validUntil ? new Date(phContract.validUntil).toISOString().split('T')[0] : undefined
                            }}
                            helperText={phEditingHoliday && role === 'affiliate' ? undefined : "Start date must be within contract period"}
                        />

                        {/* End Date */}
                        <TextField
                            label="End Date"
                            name="endDate"
                            type="date"
                            value={phData.endDate}
                            onChange={handlePhDataChange}
                            fullWidth
                            margin="normal"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                min: phData.startDate || undefined
                            }}
                            helperText="End date must be after start date"
                        />

                        {/* Show selected duration and cost */}
                        {phData.startDate && phData.endDate && (() => {
                            // Parse dates properly to avoid timezone issues
                            const startParts = phData.startDate.split('-');
                            const endParts = phData.endDate.split('-');
                            const start = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
                            const end = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));
                            
                            const durationDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                            const durationWeeks = (durationDays / 7).toFixed(1);
                            
                            // Calculate number of 31-day periods (holiday months)
                            const holidayMonths = Math.ceil(durationDays / 31);
                            const holidayFee = phContract?.affiliate?.paymentHolidayFee || 0;
                            const totalCost = holidayMonths * holidayFee;
                            
                            return (
                                <Paper 
                                    elevation={0} 
                                    sx={{ 
                                        mt: 2, 
                                        p: 2, 
                                        bgcolor: 'background.default',
                                        borderRadius: 1 
                                    }}
                                >
                                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                                        Selected duration: {durationDays} days ({durationWeeks} weeks)
                                    </Typography>
                                    
                                    {holidayFee > 0 && (
                                        <>
                                            <Divider sx={{ my: 1.5 }} />
                                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                Holiday months (31-day periods): {holidayMonths}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                Fee per month: {formatMoney(holidayFee, phContract)}
                                            </Typography>
                                            <Divider sx={{ my: 1.5 }} />
                                            <Typography variant="body1" fontWeight="bold" color="primary">
                                                Total payment holiday cost: {formatMoney(totalCost, phContract)}
                                            </Typography>
                                        </>
                                    )}
                                </Paper>
                            );
                        })()}

                        {/* Validation errors */}
                        {phValidationErrors.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                {phValidationErrors.map((error, idx) => (
                                    <Paper 
                                        key={idx}
                                        elevation={0} 
                                        sx={{ 
                                            mb: 1, 
                                            p: 1.5, 
                                            bgcolor: error.startsWith('⚠️') ? 'warning.lighter' : 'error.lighter',
                                            borderLeft: 4, 
                                            borderColor: error.startsWith('⚠️') ? 'warning.main' : 'error.main',
                                            borderRadius: 1 
                                        }}
                                    >
                                        <Typography 
                                            variant="body2" 
                                            color={error.startsWith('⚠️') ? 'warning.dark' : 'error.dark'}
                                        >
                                            {error}
                                        </Typography>
                                    </Paper>
                                ))}
                        </Box>
                        )}

                        {/* Reason Field */}
                            <TextField
                            label="Reason (optional)"
                                name="reason"
                                multiline
                                rows={3}
                                value={phData.reason}
                                onChange={handlePhDataChange}
                                fullWidth
                            margin="normal"
                            placeholder={phEditingHoliday
                                ? "Reason for this payment holiday..."
                                : "Please provide a reason for your payment holiday request..."}
                        />

                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                            <Button
                                variant="outlined"
                                onClick={handleClosePhModal}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handlePhSave}
                                startIcon={phEditingHoliday ? <EditIcon /> : <EventIcon />}
                                disabled={!phData.startDate || !phData.endDate || phValidationErrors.filter(e => !e.startsWith('⚠️')).length > 0}
                            >
                                {phEditingHoliday ? 'Save Changes' : 'Submit Request'}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Modal>

            {/* Contract end date request Modal */}
            <Modal open={terminationModalOpen} onClose={handleCloseTerminationModal}>
                <Card
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        minWidth: 400,
                        maxWidth: 600,
                        outline: 'none',
                        boxShadow: 24,
                    }}
                >
                    <CardContent>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', color: '#2c3e50', mb: 2 }}>
                            <EventBusyIcon sx={{ mr: 1 }} />
                            Request Contract End
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Submit a request to set your contract end date. The gym will review and approve or decline it.
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                            This gym requires the contract to remain valid for at least{' '}
                            <strong>{terminationMinimum.months} month{terminationMinimum.months === 1 ? '' : 's'}</strong>.
                            The earliest end date you can select is{' '}
                            <strong>{formatDate(terminationMinimum.earliestDate)}</strong>.
                        </Typography>

                        <TextField
                            label="Desired End Date"
                            name="requestedEndDate"
                            type="date"
                            value={terminationData.requestedEndDate}
                            onChange={handleTerminationDataChange}
                            fullWidth
                            margin="normal"
                            required
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                min: terminationMinimum.earliestDate,
                            }}
                            helperText={`Earliest allowed end date: ${formatDate(terminationMinimum.earliestDate)}`}
                        />

                        <TextField
                            label="Reason (optional)"
                            name="reason"
                            multiline
                            rows={3}
                            value={terminationData.reason}
                            onChange={handleTerminationDataChange}
                            fullWidth
                            margin="normal"
                            placeholder="Optionally provide a reason for ending the contract..."
                        />

                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                            <Button
                                variant="outlined"
                                onClick={handleCloseTerminationModal}
                                disabled={submittingTermination}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleTerminationSave}
                                startIcon={submittingTermination ? <CircularProgress size={18} color="inherit" /> : <EventBusyIcon />}
                                disabled={!terminationData.requestedEndDate || submittingTermination}
                            >
                                Submit Request
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Modal>

            {/* Termination approval follow-up: check payment link */}
            <Dialog
                open={terminationFollowUpOpen}
                onClose={handleCloseTerminationFollowUp}
            >
                <DialogTitle>Check Payment Link</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The contract end date has been saved. Please check whether the member needs a new payment link.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTerminationFollowUp} color="inherit" variant="outlined">
                        OK
                    </Button>
                    <Button
                        onClick={handleTerminationFollowUpSendLink}
                        color="primary"
                        variant="contained"
                        startIcon={<SendIcon />}
                    >
                        Send Payment Link
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openDeactivateDialog}
                onClose={handleCancelDeactivate}
                aria-labelledby="deactivate-dialog-title"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: 500
                    }
                }}
            >
                <DialogTitle id="deactivate-dialog-title" sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: '#f5f5f5',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <CalendarMonthIcon sx={{ mr: 1, color: 'secondary.main' }} />
                    Set New End Date
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <DialogContentText>
                        Are you sure you want to change the end date for this contract?
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="New End Date"
                        type="date"
                        fullWidth
                        value={newEndDate}
                        onChange={(e) => setNewEndDate(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        onClick={handleCancelDeactivate}
                        color="inherit"
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDeactivate}
                        color="secondary"
                        disabled={!newEndDate}
                        variant="contained"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Success Dialog */}
            <Dialog
                open={successDialogOpen}
                onClose={() => setSuccessDialogOpen(false)}
            >
                <DialogTitle>Success</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {dialogMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSuccessDialogOpen(false)} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Error Dialog */}
            <Dialog
                open={errorDialogOpen}
                onClose={() => setErrorDialogOpen(false)}
            >
                <DialogTitle>Error</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {dialogMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setErrorDialogOpen(false)} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
            >
                <DialogTitle>{confirmDialogTitle}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmDialogMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (confirmAction) confirmAction();
                            setConfirmDialogOpen(false);
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={phFollowUpModalOpen}
                onClose={() => !processingPhFollowUp && handleClosePhFollowUpModal()}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                    Payment Holiday Request Confirmed
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <DialogContentText sx={{ mb: 2, color: 'success.main', fontWeight: 600 }}>
                        Request confirmed.
                    </DialogContentText>

                    {phFollowUpData && (
                        <>
                            <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                                <Typography variant="body2">
                                    Period: <strong>{formatDate(phFollowUpData.periodStart)} - {formatDate(phFollowUpData.periodEnd)}</strong>
                                </Typography>
                            </Paper>

                            {phFollowUpData.hasPaidInPeriod && (
                                <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#edf7ed', border: '1px solid #c8e6c9' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                                        User has paid in one or more affected billing periods.
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        Proportional refundable amount (from contract monthly fee): <strong>{formatMoney(phFollowUpData.refundableBaseAmount, phFollowUpData.contract)}</strong>
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                                        Payment holiday fee (deducted once): <strong>{formatMoney(phFollowUpData.holidayFee, phFollowUpData.contract)}</strong>
                                    </Typography>

                                    <FormControl component="fieldset" sx={{ mb: 2 }}>
                                        <FormLabel component="legend">Step 1: Return paid amount as credit?</FormLabel>
                                        <RadioGroup
                                            value={returnAsCredit}
                                            onChange={(e) => setReturnAsCredit(e.target.value)}
                                        >
                                            <FormControlLabel value="yes" control={<Radio />} label="Yes, return as credit" />
                                            <FormControlLabel value="no" control={<Radio />} label="No, do not return as credit" />
                                        </RadioGroup>
                                    </FormControl>

                                    {returnAsCredit === 'yes' && !phFollowUpData.skipFeeDeductionQuestion && (
                                        <FormControl component="fieldset">
                                            <FormLabel component="legend">Step 2: Deduct payment holiday fee from returned amount?</FormLabel>
                                            <RadioGroup
                                                value={deductHolidayFee}
                                                onChange={(e) => setDeductHolidayFee(e.target.value)}
                                            >
                                                <FormControlLabel value="yes" control={<Radio />} label="Yes, deduct holiday fee" />
                                                <FormControlLabel value="no" control={<Radio />} label="No, return full amount" />
                                            </RadioGroup>
                                        </FormControl>
                                    )}

                                    {returnAsCredit === 'yes' && phFollowUpData.skipFeeDeductionQuestion && (
                                        <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                                            Payment holiday is shorter than one month and extends into the next billing period. The payment holiday fee will be applied in the next month, so fee deduction is not asked in this step.
                                        </Typography>
                                    )}

                                    <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 700 }}>
                                        Refundable amount with current selection: <strong>{formatMoney(refundableAmountWithCurrentFeeSetting, phFollowUpData.contract)}</strong>
                                    </Typography>
                                </Paper>
                            )}

                            {phFollowUpData.hasPendingInPeriod && (
                                <Paper elevation={0} sx={{ p: 2, mb: 1, bgcolor: '#fff3e0', border: '1px solid #ffcc80' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                                        User has a pending payment link for the current affected period.
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                                        Do you want to send a new payment link for this period?
                                    </Typography>

                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            value={sendNewPaymentLinkChoice}
                                            onChange={(e) => setSendNewPaymentLinkChoice(e.target.value)}
                                        >
                                            <FormControlLabel value="yes" control={<Radio />} label="Yes, send new payment link" />
                                            <FormControlLabel value="no" control={<Radio />} label="No, keep current pending link" />
                                        </RadioGroup>
                                    </FormControl>
                                </Paper>
                            )}

                            {phFollowUpData.hasPendingInNextPeriod && (
                                <Paper elevation={0} sx={{ p: 2, mb: 1, bgcolor: '#fff8e1', border: '1px solid #ffe082' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                                        A pending payment link also exists for the next period.
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                                        Do you also want to send a new payment link for the next period?
                                    </Typography>

                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            value={sendNextPeriodPaymentLinkChoice}
                                            onChange={(e) => setSendNextPeriodPaymentLinkChoice(e.target.value)}
                                        >
                                            <FormControlLabel value="yes" control={<Radio />} label="Yes, send new link for next period" />
                                            <FormControlLabel value="no" control={<Radio />} label="No, keep current next-period pending link" />
                                        </RadioGroup>
                                    </FormControl>
                                </Paper>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        onClick={handleClosePhFollowUpModal}
                        color="inherit"
                        variant="outlined"
                        disabled={processingPhFollowUp}
                    >
                        Close
                    </Button>
                    <Button
                        onClick={handleConfirmPhFollowUp}
                        color="primary"
                        variant="contained"
                        disabled={processingPhFollowUp}
                        startIcon={processingPhFollowUp ? <CircularProgress size={16} /> : null}
                    >
                        {processingPhFollowUp ? 'Saving...' : 'Confirm choices'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={sendLinkDialogOpen}
                onClose={() => !sendingPaymentLink && setSendLinkDialogOpen(false)}
                aria-labelledby="send-link-dialog-title"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: 700
                    }
                }}
            >
                <DialogTitle id="send-link-dialog-title" sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: '#f5f5f5',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <SendIcon sx={{ mr: 1, color: 'primary.main' }} />
                    Send Payment Link
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <DialogContentText sx={{ mb: 2 }}>
                        Select the month for which you want to send the payment link:
                    </DialogContentText>

                    {contractToSendLink && (
                        <>
                            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                                <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 2 }}>
                                    Select Payment Period:
                                </FormLabel>
                                <RadioGroup
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                    sx={{ ml: 1 }}
                                >
                                    <FormControlLabel
                                        value="previous"
                                        control={<Radio />}
                                        label={
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    Previous Period
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {paymentPeriods.previous ? paymentPeriods.previous.label : 'Calculating...'}
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ mb: 1, alignItems: 'flex-start' }}
                                    />
                                    <FormControlLabel
                                        value="current"
                                        control={<Radio />}
                                        label={
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    Current Period
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {paymentPeriods.current ? paymentPeriods.current.label : 'Calculating...'}
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ mb: 1, alignItems: 'flex-start' }}
                                    />
                                    <FormControlLabel
                                        value="next"
                                        control={<Radio />}
                                        label={
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    Next Period
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {paymentPeriods.next ? paymentPeriods.next.label : 'Calculating...'}
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ alignItems: 'flex-start' }}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </>
                    )}

                    <Box sx={{
                        bgcolor: '#fff3e0',
                        p: 2,
                        borderRadius: 1,
                        border: '1px solid #ffb74d'
                    }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e65100', mb: 1 }}>
                            ⚠️ Important Notice:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#e65100' }}>
                            Clicking "Send Payment Link" will automatically cancel any existing pending payment transactions for this contract and create a new payment link for the selected month. The system will check for available user credit and payment holidays before generating the link.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        onClick={() => setSendLinkDialogOpen(false)}
                        color="inherit"
                        variant="outlined"
                        disabled={sendingPaymentLink}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSendPaymentLink}
                        color="primary"
                        variant="contained"
                        disabled={sendingPaymentLink}
                        startIcon={sendingPaymentLink ? <CircularProgress size={16} /> : <SendIcon />}
                    >
                        {sendingPaymentLink ? 'Sending...' : `Send Payment Link for ${
                            selectedMonth === 'previous' ? 'Previous' : 
                            selectedMonth === 'current' ? 'Current' : 
                            'Next'
                        } Period`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}